import { useEffect, useRef, useState } from 'react';

const COMPANY_INFO = [
  { label: '会社名', value: '株式会社OTAKE' },
  { label: '代表取締役', value: '永井 健男' },
  { label: '従業員数', value: '9名' },
  { label: '所在地', value: '岡山県岡山市中区倉田652番地15' },
  { label: '電話番号', value: '086-238-7864' },
  { label: '営業時間', value: '9:00〜17:00' },
  { label: '定休日', value: '土曜日・日曜日・祝日' },
  { label: '事業内容', value: '貴金属・腕時計・ブランド品・家電・家具・記念硬貨・古銭・切手等の買取業、\n遺品・生前整理業、オークション事業' },
];

const LICENSES = [
  { icon: 'ri-shield-check-line', name: '古物商許可証', sub: '岡山県公安委員会許可', num: '古物商番号　第721010030376 号' },
];

const STATS = [
  { value: '9', unit: '名', label: 'スタッフ数' },
  { value: '西日本', unit: '中心', label: 'サービスエリア' },
  { value: '100', unit: '点以上', label: '取扱い品目' },
  { value: '多数', unit: '', label: '知識豊富なスタッフ在籍' },
];

const AboutSection = () => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" ref={ref} className="py-20 md:py-28 bg-[#F8F9F7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className={`text-center mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-[#B2AC88] text-xs font-medium tracking-widest uppercase">About Us</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#2F3630] mt-3 mb-4" style={{ fontFamily: "'Shippori Mincho', serif" }}>
            会社概要
          </h2>
        </div>

        {/* Mission + Image */}
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 mb-16 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Image */}
          <div className="relative rounded-2xl overflow-hidden h-72 sm:h-80 lg:h-auto">
            <img
              src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/c414409f7a1429d6a9a3226f28cf221b.webp"
              alt="株式会社OTAKEのスタッフ5名 岡山市の買取専門店"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/20 via-[15%] to-transparent to-[35%]" />
            <div className="absolute bottom-6 left-6">
              <p className="text-[#B2AC88]/80 text-xs tracking-widest uppercase mb-1">Our Team</p>
              <p className="text-white font-bold text-xl" style={{ fontFamily: "'Shippori Mincho', serif" }}>私たちについて</p>
            </div>
          </div>

          {/* Mission Text */}
          <div className="flex flex-col justify-center">
            <h3 className="text-xl md:text-2xl font-bold text-[#2F3630] mb-5 leading-snug" style={{ fontFamily: "'Shippori Mincho', serif" }}>
              地球にやさしい循環型社会の実現へ
            </h3>
            <p className="text-gray-600 text-sm md:text-base leading-relaxed mb-5">
              株式会社OTAKEは岡山市中区で設立された買取専門会社です。
              「使わなくなったものに新しい価値を」というコンセプトのもと、
              貴金属・腕時計・ブランド品・家電・家具・記念硬貨・古銭・切手など幅広い品目の買取を行っています。
            </p>
            <p className="text-gray-600 text-sm md:text-base leading-relaxed mb-6">
              西日本を中心に、近隣市町村のお客様にも丁寧で迅速な対応を心がけています。
              専門知識を持つスタッフが一点一点丁寧に査定し、お客様に満足いただける高価買取を目指しています。
            </p>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4">
              {STATS.map((stat) => (
                <div key={stat.label} className="bg-white rounded-xl p-4 text-center">
                  <p className="text-[#B2AC88] font-bold text-2xl md:text-3xl">
                    {stat.value}<span className="text-lg">{stat.unit}</span>
                  </p>
                  <p className="text-gray-500 text-xs mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Company Info Table + Licenses */}
        <div className={`grid grid-cols-1 lg:grid-cols-3 gap-8 transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Table */}
          <div className="lg:col-span-2 bg-white rounded-2xl p-6 md:p-8">
            <h3 className="text-lg font-bold text-[#2F3630] mb-6 flex items-center gap-2">
              <i className="ri-building-2-line text-[#B2AC88]" />
              会社情報
            </h3>
            <dl>
              {COMPANY_INFO.map((item, i) => (
                <div
                  key={item.label}
                  className={`flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 py-3 ${
                    i < COMPANY_INFO.length - 1 ? 'border-b border-gray-100' : ''
                  }`}
                >
                  <dt className="text-gray-400 text-xs font-medium w-24 flex-shrink-0">{item.label}</dt>
                  <dd className="text-gray-800 text-sm font-medium whitespace-pre-line">{item.value}</dd>
                </div>
              ))}
            </dl>
          </div>

          {/* Licenses */}
          <div className="bg-white rounded-2xl p-6 md:p-8 flex flex-col">
            <h3 className="text-lg font-bold text-[#2F3630] mb-6 flex items-center gap-2">
              <i className="ri-award-line text-[#B2AC88]" />
              資格
            </h3>
            <div className="flex-1 flex flex-col justify-center">
              {LICENSES.map((lic) => (
                <div key={lic.name} className="flex flex-col items-center text-center gap-6 py-4">
                  {/* Icon */}
                  <div className="w-20 h-20 flex items-center justify-center bg-[#B2AC88] rounded-2xl flex-shrink-0">
                    <i className={`${lic.icon} text-white text-4xl`} />
                  </div>
                  {/* Name */}
                  <div>
                    <p className="text-[#2F3630] font-bold text-xl mb-1">{lic.name}</p>
                    <p className="text-gray-500 text-sm">{lic.sub}</p>
                  </div>
                  {/* Number */}
                  <div className="w-full bg-[#B2AC88]/10 rounded-xl px-4 py-4">
                    <p className="text-[#2F3630] font-medium text-sm leading-relaxed">{lic.num}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
